import { useCallback, useState } from 'react'
import { useI18n } from '../../i18n'
import {
  fetchAssessmentHistory,
  formatScannerSourceLabel,
  type AssessmentHistoryEntry,
} from '../../services/vulnerabilityPostureApi'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'
import styles from '../../styles/pages/Reports.module.css'

export type FilteredVulnerabilityRow = {
  id: number | string
  name: string
  severity: string
  cvss?: number | null
  cve?: string | null
  asset?: string | null
  status?: string
  firstDetectedAt?: string | null
  lastDetectedAt?: string | null
  detectedBy?: string[]
  sourceProvider?: string | null
  normalizedFingerprint?: string | null
  assetKey?: string | null
  assessmentCount?: number
  affectedInstancesCount?: number
  instances?: string[]
}

type Props = {
  items: FilteredVulnerabilityRow[]
  loading?: boolean
}

function formatDay(value: string | null | undefined) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleDateString()
  } catch {
    return value
  }
}

function formatDetectedBy(item: FilteredVulnerabilityRow) {
  if (item.detectedBy?.length) {
    return item.detectedBy.map((s) => formatScannerSourceLabel(s)).join(' · ')
  }
  if (item.sourceProvider?.includes(',')) {
    return item.sourceProvider.split(',').map((s) => formatScannerSourceLabel(s.trim())).join(' · ')
  }
  return item.sourceProvider ? formatScannerSourceLabel(item.sourceProvider) : '—'
}

function severityBadgeClass(severity: string) {
  const s = String(severity || '').toUpperCase()
  if (s === 'CRITICAL') return entStyles.badgeRisk
  if (s === 'HIGH') return entStyles.badgeWarn
  if (s === 'MEDIUM') return entStyles.badge
  return entStyles.badgeStable
}

export default function FilteredVulnerabilitiesList({ items, loading }: Props) {
  const { t } = useI18n()
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [historyById, setHistoryById] = useState<Record<string, AssessmentHistoryEntry[]>>({})
  const [historyLoading, setHistoryLoading] = useState<string | null>(null)

  const loadHistory = useCallback(async (item: FilteredVulnerabilityRow) => {
    const key = String(item.id)
    if (historyById[key]) return
    setHistoryLoading(key)
    try {
      const rows = await fetchAssessmentHistory({
        assetKey: item.assetKey,
        fingerprint: item.normalizedFingerprint,
        title: item.name,
      })
      setHistoryById((prev) => ({ ...prev, [key]: rows }))
    } catch {
      setHistoryById((prev) => ({ ...prev, [key]: [] }))
    } finally {
      setHistoryLoading(null)
    }
  }, [historyById])

  const toggle = (item: FilteredVulnerabilityRow) => {
    const key = String(item.id)
    if (expandedId === key) {
      setExpandedId(null)
      return
    }
    setExpandedId(key)
    void loadHistory(item)
  }

  if (loading) {
    return <p className={entStyles.emptyState}>{t('common.loading', 'Loading…')}</p>
  }

  if (items.length === 0) {
    return (
      <p className={entStyles.emptyState}>
        {t('reports.noVulnerabilities', 'No vulnerabilities match your filters')}
      </p>
    )
  }

  return (
    <div className={styles.filteredVulnList}>
      {items.map((item) => {
        const key = String(item.id)
        const open = expandedId === key
        const history = historyById[key] || []

        return (
          <article key={key} className={`${entStyles.layer} ${styles.filteredVulnCard}`}>
            <button type="button" className={styles.filteredVulnSummary} onClick={() => toggle(item)}>
              <div className={styles.filteredVulnSummaryMain}>
                <p className={styles.filteredVulnTitle}>{item.name}</p>
                <p className={styles.filteredVulnMeta}>
                  {item.asset || '—'}
                  {' · '}
                  <span className={severityBadgeClass(item.severity)}>{item.severity}</span>
                  {item.cve ? ` · ${item.cve}` : ''}
                  {' · '}
                  {formatDetectedBy(item)}
                  {item.affectedInstancesCount != null
                    ? ` · ${item.affectedInstancesCount} ${t('reports.affectedInstances', 'affected instances')}`
                    : ''}
                  {item.assessmentCount != null ? ` · ${item.assessmentCount} assessments` : ''}
                </p>
              </div>
              <span className={styles.filteredVulnHint}>
                {open ? t('common.collapse', 'Collapse') : t('reports.viewFindingDetails', 'View details')}
              </span>
            </button>

            {open ? (
              <div className={`${entStyles.posturePanel} ${styles.filteredVulnBody}`}>
                <div className={entStyles.postureGrid}>
                  <div className={entStyles.postureMetric}>
                    <p className={entStyles.postureMetricLabel}>{t('reports.asset', 'Asset')}</p>
                    <p className={entStyles.postureMetricValue}>{item.asset || '—'}</p>
                  </div>
                  <div className={entStyles.postureMetric}>
                    <p className={entStyles.postureMetricLabel}>{t('reports.riskLevel', 'Severity')}</p>
                    <p className={entStyles.postureMetricValue}>{item.severity || '—'}</p>
                  </div>
                  <div className={entStyles.postureMetric}>
                    <p className={entStyles.postureMetricLabel}>{t('reports.detectedBy', 'Detected by')}</p>
                    <p className={entStyles.postureMetricValue}>{formatDetectedBy(item)}</p>
                  </div>
                  <div className={entStyles.postureMetric}>
                    <p className={entStyles.postureMetricLabel}>
                      {t('reports.affectedInstances', 'Affected instances')}
                    </p>
                    <p className={entStyles.postureMetricValue}>{item.affectedInstancesCount ?? 1}</p>
                  </div>
                  <div className={entStyles.postureMetric}>
                    <p className={entStyles.postureMetricLabel}>{t('reports.firstSeen', 'First seen')}</p>
                    <p className={entStyles.postureMetricValue}>{formatDay(item.firstDetectedAt)}</p>
                  </div>
                  <div className={entStyles.postureMetric}>
                    <p className={entStyles.postureMetricLabel}>{t('reports.detected', 'Last detected')}</p>
                    <p className={entStyles.postureMetricValue}>{formatDay(item.lastDetectedAt)}</p>
                  </div>
                  <div className={entStyles.postureMetric}>
                    <p className={entStyles.postureMetricLabel}>{t('reports.assessmentCount', 'Assessment count')}</p>
                    <p className={entStyles.postureMetricValue}>
                      {historyById[key]?.length || item.assessmentCount || 1}
                    </p>
                  </div>
                  {item.cvss != null ? (
                    <div className={entStyles.postureMetric}>
                      <p className={entStyles.postureMetricLabel}>CVSS</p>
                      <p className={entStyles.postureMetricValue}>{item.cvss}</p>
                    </div>
                  ) : null}
                </div>

                {item.instances && item.instances.length > 0 ? (
                  <section className={entStyles.layer}>
                    <p className={entStyles.layerLabel}>
                      {t('reports.instancesSection', 'Instances')}
                    </p>
                    <ul className={styles.assessmentHistoryList}>
                      {item.instances.slice(0, 20).map((loc) => (
                        <li key={loc} className={styles.assessmentHistoryItem}>
                          <span className="font-mono text-xs">{loc}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null}

                <section className={entStyles.layer}>
                  <p className={entStyles.layerLabel}>
                    {t('reports.assessmentHistory', 'Assessment history')}
                  </p>
                  {historyLoading === key ? (
                    <p className={entStyles.emptyState}>{t('common.loading', 'Loading…')}</p>
                  ) : history.length === 0 ? (
                    <p className={entStyles.emptyState}>{t('common.noData', 'No data available')}</p>
                  ) : (
                    <ul className={styles.assessmentHistoryList}>
                      {history.map((h) => (
                        <li key={`${h.assessmentId}-${h.assessmentAt}`} className={styles.assessmentHistoryItem}>
                          <span>{formatScannerSourceLabel(h.providerType || 'UNKNOWN')}</span>
                          <span>{formatDay(h.assessmentAt)}</span>
                          <span>{h.severity}</span>
                          <span>
                            {h.instanceCount != null && h.instanceCount > 0
                              ? `${h.instanceCount} instance${h.instanceCount === 1 ? '' : 's'}`
                              : h.title}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </div>
            ) : null}
          </article>
        )
      })}
    </div>
  )
}
